import { useEffect, useState, useRef, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { EduViewer, chunkedUpload } from "@/components/common/EduViewer";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { GraduationCap, FolderPlus, Upload, Link2, Trash2, FileText, Video, ImageIcon, ExternalLink, Loader2, Folder } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { pdf: FileText, video: Video, image: ImageIcon, link: Link2, file: FileText };

export default function PatientEducation() {
  const [folders, setFolders] = useState([]);
  const [resources, setResources] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folderOpen, setFolderOpen] = useState(false);
  const [newFolder, setNewFolder] = useState({ name: "", condition_slug: "" });
  const [linkOpen, setLinkOpen] = useState(null); // folder object
  const [newLink, setNewLink] = useState({ title: "", url: "" });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [viewing, setViewing] = useState(null);
  const { user } = useAuth();
  const admin = user?.is_admin || ["admin", "owner"].includes((user?.role || "").toLowerCase());
  const canEdit = (x) => admin || x.owner_id === user?.id;
  const folderOf = (id) => folders.find((f) => f.id === id) || {};
  const canDownload = (r) => !!(r.downloadable || folderOf(r.folder_id).downloadable);
  const setDl = async (kind, x, v) => { try { await api.put(`/education/${kind}/${x.id}/download`, { downloadable: v }); toast.success(v ? "Downloads allowed" : "Downloads turned off"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change download access"); } };
  const fileRef = useRef(null);
  const uploadFolder = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [f, r] = await Promise.all([api.get("/education/folders"), api.get("/education/resources")]);
      setFolders(f.data || []); setResources(r.data || []);
    } catch { toast.error("Could not load education library"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); api.get("/conditions").then((r) => setConditions(r.data)).catch(() => {}); }, []);

  const byFolder = useMemo(() => { const m = {}; resources.forEach((r) => { (m[r.folder_id] = m[r.folder_id] || []).push(r); }); return m; }, [resources]);

  const createFolder = async () => {
    if (!newFolder.name.trim()) { toast.error("Folder name is required"); return; }
    const cond = conditions.find((c) => c.slug === newFolder.condition_slug);
    try {
      await api.post("/education/folders", { name: newFolder.name, condition_slug: newFolder.condition_slug, condition_name: cond?.name || "" });
      toast.success("Folder created"); setFolderOpen(false); setNewFolder({ name: "", condition_slug: "" }); load();
    } catch { toast.error("Could not create folder"); }
  };
  const deleteFolder = async (id) => {
    if (!window.confirm("Delete this folder and all its resources?")) return;
    try { await api.delete(`/education/folders/${id}`); toast.success("Folder deleted"); load(); } catch { toast.error("Could not delete"); }
  };
  const triggerUpload = (folder) => { uploadFolder.current = folder; fileRef.current?.click(); };
  const doUpload = async (file) => {
    if (!file) return;
    const folder = uploadFolder.current; setBusy(true); setProgress({ name: file.name, pct: 0 });
    try {
      await chunkedUpload(file, folder, (pct) => setProgress({ name: file.name, pct }));
      toast.success("Resource uploaded"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload interrupted — choose the same file again to resume"); }
    finally { setBusy(false); setProgress(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  const addLink = async () => {
    if (!newLink.url.trim()) { toast.error("URL is required"); return; }
    try {
      await api.post("/education/resources/link", { folder_id: linkOpen.id, condition_slug: linkOpen.condition_slug || "", title: newLink.title || newLink.url, url: newLink.url });
      toast.success("Link added"); setLinkOpen(null); setNewLink({ title: "", url: "" }); load();
    } catch { toast.error("Could not add link"); }
  };
  const deleteResource = async (id) => { try { await api.delete(`/education/resources/${id}`); load(); } catch { toast.error("Could not delete"); } };
  const openResource = (r) => setViewing(r);

  return (
    <div>
      <PageHeader title="Patient Education" subtitle="Organize educational content by condition — auto-surfaced on tagged patients"
        breadcrumb={["Clinical", "Patient Education"]}
        actions={<Button onClick={() => setFolderOpen(true)} data-testid="edu-new-folder" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg"><FolderPlus className="h-4 w-4 mr-1.5" />New folder</Button>}
      />
      <input ref={fileRef} type="file" className="hidden" accept=".pdf,.mp4,.mov,.webm,.m4v,.png,.jpg,.jpeg,.webp,.gif" onChange={(e) => doUpload(e.target.files?.[0])} data-testid="edu-file-input" />
      {progress && <SurfaceCard className="mb-4 p-4" data-testid="edu-upload-progress"><div className="mb-2 flex justify-between text-sm"><span className="break-all">Uploading {progress.name}</span><b>{progress.pct}%</b></div><Progress value={progress.pct} /></SurfaceCard>}
      {viewing && <EduViewer r={viewing} canDownload={canDownload(viewing)} onClose={() => setViewing(null)} />}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[0, 1].map((i) => <SurfaceCard key={i} className="h-40 animate-pulse" />)}</div>
      ) : folders.length === 0 ? (
        <EmptyState icon={GraduationCap} title="No education folders yet" description="Create folders by condition (e.g. Implant, TMJ), then upload PDFs, videos or images. Whenever a patient is tagged with that condition, this content appears in their Education tab." actionLabel="Create your first folder" onAction={() => setFolderOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {folders.map((f) => {
            const items = byFolder[f.id] || [];
            return (
              <SurfaceCard key={f.id} className="p-5" data-testid={`edu-folder-${f.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#00A85A]/12"><Folder className="h-4 w-4 text-[#00A85A]" /></div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-[#101516] dark:text-white truncate">{f.name}</div>
                      {f.condition_name && <span className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">{f.condition_name}</span>}
                      {f.shared && <span className="ml-2 rounded-full bg-[#E8F7F0] px-2 text-[10px] font-bold text-[#008F4D]" data-testid={`edu-shared-${f.id}`}>Shared library</span>}
                      <label className="mt-1 flex items-center gap-1.5 text-xs text-[#687173]"><Switch checked={!!f.downloadable} disabled={!canEdit(f)} onCheckedChange={(v) => setDl("folders", f, v)} data-testid={`edu-folder-dl-${f.id}`} />Allow downloads</label>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {canEdit(f) && <><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => triggerUpload(f)} data-testid={`edu-upload-${f.id}`} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}</Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setLinkOpen(f); setNewLink({ title: "", url: "" }); }} data-testid={`edu-link-${f.id}`}><Link2 className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => deleteFolder(f.id)} data-testid={`edu-del-folder-${f.id}`}><Trash2 className="h-4 w-4" /></Button></>}
                  </div>
                </div>
                {items.length === 0 ? (
                  <p className="text-xs text-[#8B9698] py-3">No resources yet. Upload a PDF/video/image or add a link.</p>
                ) : (
                  <div className="space-y-1.5">
                    {items.map((r) => { const Icon = TYPE_ICON[r.type] || FileText; return (
                      <div key={r.id} className="flex items-center gap-2 rounded-lg border border-[#E2E8F0] dark:border-[#223139] px-3 py-2" data-testid={`edu-res-${r.id}`}>
                        <Icon className="h-4 w-4 text-[#00A85A] shrink-0" />
                        <button onClick={() => openResource(r)} className="flex-1 min-w-0 text-left text-sm text-[#101516] dark:text-white break-words hover:text-[#00A85A]" data-testid={`edu-open-${r.id}`}>{r.title}</button>
                        {r.type !== "link" && <Switch checked={!!r.downloadable} disabled={!canEdit(r)} onCheckedChange={(v) => setDl("resources", r, v)} title="Allow download" data-testid={`edu-res-dl-${r.id}`} />}
                        <button onClick={() => openResource(r)} className="text-[#8B9698] hover:text-[#00A85A]"><ExternalLink className="h-3.5 w-3.5" /></button>
                        {canEdit(r) && <button onClick={() => deleteResource(r.id)} className="text-[#8B9698] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </div>
                    ); })}
                  </div>
                )}
              </SurfaceCard>
            );
          })}
        </div>
      )}

      <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edu-folder-dialog">
          <DialogHeader><DialogTitle>New education folder</DialogTitle><DialogDescription>Group content by clinical condition so it auto-surfaces on tagged patients.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Folder name</Label><Input value={newFolder.name} onChange={(e) => setNewFolder((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Implant aftercare" data-testid="edu-folder-name" /></div>
            <div className="space-y-1.5"><Label>Condition category</Label>
              <Select value={newFolder.condition_slug} onValueChange={(v) => setNewFolder((f) => ({ ...f, condition_slug: v }))}>
                <SelectTrigger data-testid="edu-folder-condition"><SelectValue placeholder="Select a condition" /></SelectTrigger>
                <SelectContent>{conditions.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setFolderOpen(false)} className="rounded-lg">Cancel</Button><Button onClick={createFolder} data-testid="edu-folder-create" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">Create folder</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkOpen} onOpenChange={(o) => !o && setLinkOpen(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edu-link-dialog">
          <DialogHeader><DialogTitle>Add an external file or folder</DialogTitle><DialogDescription>Paste a public web address (e.g. Hostinger). It stays on that server and plays inside CONTAHA.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Title</Label><Input value={newLink.title} onChange={(e) => setNewLink((l) => ({ ...l, title: e.target.value }))} placeholder="What is a dental implant?" data-testid="edu-link-title" /></div>
            <div className="space-y-1.5"><Label>URL</Label><Input value={newLink.url} onChange={(e) => setNewLink((l) => ({ ...l, url: e.target.value }))} placeholder="https://..." data-testid="edu-link-url" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLinkOpen(null)} className="rounded-lg">Cancel</Button><Button onClick={addLink} data-testid="edu-link-add" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">Add link</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
